import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Building2, Package, Wrench, Truck, FileCheck, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SUPPLIER_CATEGORIES = [
  { icon: Package, label: "Materials & Equipment", desc: "Steel, concrete, MEP equipment, tools" },
  { icon: Wrench, label: "Specialised Services", desc: "Welding, scaffolding, NDT, surveying" },
  { icon: Truck, label: "Logistics & Transport", desc: "Heavy haulage, crane hire, fleet" },
  { icon: ShieldCheck, label: "Safety & PPE", desc: "Hi-vis, harnesses, fire equipment" },
  { icon: Building2, label: "Subcontracting", desc: "Civil, MEP, finishing trades" },
  { icon: FileCheck, label: "Consulting & Advisory", desc: "Audits, certifications, training" },
];

const empty = {
  company_name: "",
  contact_person: "",
  email: "",
  phone: "",
  cr_number: "",
  category: "",
  description: "",
  country: "Saudi Arabia",
};

export default function SuppliersPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person || !form.email || !form.phone || !form.category) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/suppliers", form);
      toast.success("Supplier registration received. Procurement will contact you within 5 business days.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="suppliers-page">
      <TopBar />
      <Navbar />

      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground grain pt-32 pb-20">
        <div className="container-page">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-secondary-foreground/60 hover:text-primary transition-colors" data-testid="suppliers-back-home">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="overline text-primary">/ Supplier Portal</span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05]">
                Build with us. <br />
                <span className="text-primary">Supply MNH.</span>
              </h1>
            </div>
            <p className="lg:col-span-4 text-secondary-foreground/65 leading-relaxed">
              Register as a vendor for materials, services, subcontracting, logistics, or consulting. Our procurement team reviews every submission within 5 business days.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container-page py-20">
        <span className="overline">/ Vendor Categories</span>
        <h2 className="mt-4 text-3xl sm:text-4xl font-black mb-10">What we source.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suppliers-categories">
          {SUPPLIER_CATEGORIES.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="bg-card border border-border p-6 hover:border-primary transition-colors"
              data-testid={`supplier-cat-${i}`}
            >
              <div className="h-11 w-11 bg-secondary text-primary flex items-center justify-center">
                <c.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-bold">{c.label}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Registration Form */}
      <section className="bg-muted/40 border-y border-border py-20">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">/ Vendor Registration</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black leading-tight">
              Submit your <span className="text-primary">company profile.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Our procurement team uses a transparent pre-qualification process aligned with Saudi Vision 2030, Aramco Wamada, and ISNetworld standards.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/80">
              <li className="flex items-start gap-3"><FileCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Saudi C.R., VAT, Saudization &amp; HSE certificates required</li>
              <li className="flex items-start gap-3"><FileCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Past performance on enterprise projects</li>
              <li className="flex items-start gap-3"><FileCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Financial standing &amp; bank references</li>
            </ul>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-secondary text-secondary-foreground p-8 sm:p-10 grain space-y-5" data-testid="supplier-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <FormField label="Company Name *">
                <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-company" required />
              </FormField>
              <FormField label="C.R. Number">
                <Input value={form.cr_number} onChange={(e) => update("cr_number", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-cr" />
              </FormField>
              <FormField label="Contact Person *">
                <Input value={form.contact_person} onChange={(e) => update("contact_person", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-contact" required />
              </FormField>
              <FormField label="Country">
                <Input value={form.country} onChange={(e) => update("country", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-country" />
              </FormField>
              <FormField label="Email *">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-email" required />
              </FormField>
              <FormField label="Phone *">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="supplier-phone" required />
              </FormField>
            </div>
            <FormField label="Vendor Category *">
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger className="bg-transparent border-white/15 text-white rounded-sm h-11 data-[placeholder]:text-white/40" data-testid="supplier-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {SUPPLIER_CATEGORIES.map((c, i) => (<SelectItem key={i} value={c.label}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Capabilities & Past Projects">
              <Textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm" placeholder="Tell us about your strengths, certifications, and notable projects." data-testid="supplier-description" />
            </FormField>
            <Button type="submit" disabled={loading} size="lg" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12" data-testid="supplier-submit">
              {loading ? "Submitting…" : "Submit Registration"}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-secondary-foreground/80 mb-2 block text-xs uppercase tracking-[0.2em]">{label}</Label>
      {children}
    </div>
  );
}
